import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { FieldValues, useFormContext, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useGetIndustryListForDropdowns } from "../../../../api/services/client/clientApis";
import { useGetCountryListByPage } from "../../../../api/services/common/commonApis";
import { useGetDeliveryOfficeListQuery } from "../../../../api/services/mdmOffice/mdmOfficeApis";
import { DeliveryOfficeListItem } from "../../../../api/services/mdmOffice/models";
import { useGetUsersDetailByRoleMutation } from "../../../../api/services/user/userApi";
import {
  FormController,
  MuiBox,
  MuiGrid,
  MuiSelect,
  MuiTextField,
  OptionsDataType,
} from "../../../../components";
import {
  CountryField,
  CountryFieldDetails,
  TextFieldWithValidation,
} from "../../../../components/textFieldWithValidation/TextFieldWithValidation";
import {
  MAX_LENGTH_100,
  MAX_LENGTH_11,
  MAX_LENGTH_13,
  MAX_LENGTH_14,
  MAX_LENGTH_14_INCLUDING_SPACE,
  MAX_LENGTH_21,
  MAX_LENGTH_50,
  MAX_LENGTH_9,
  PAGE_IDS,
  PAGE_NAME_FOR_COUNTRY_DROPDOWN,
  PAGE_OPERATION_TYPE,
  REGEX_ALL_ZERO,
  REGEX_ALLOW_ALPHA_NUMERIC_WITHOUT_SPECIAL_CHAR,
  REGEX_ALLOW_ALPHABET_SPECIAL_CHAR,
  REGEX_ALLOW_NUMBER,
  REGEX_ALLOW_SPECIAL_CHAR,
} from "../../../../helpers/constants";
import {
  FORM_ERROR_TIMEOUT_LONG,
  FORM_ERROR_TIMEOUT_SHORT,
  LANGUAGE_CODE,
  PATTERN,
  ROLE_IDS,
  SITE_CORE_COUNTRIES_VALUE,
} from "../../../../helpers/constants/common";
import { commonSiteCoreKeys } from "../../../../helpers/constants/siteCoreKeys";
import {
  checkWithLowerCase,
  formatApiDataToDropDownOption,
  getCountryCodeWithoutCountryPrefix,
  getValueOrBlank,
  getVatPostalDetailsFromCountryKey,
  isEmpty,
  isNotEmpty,
  removeSpecialCharacters,
  removeWhiteSpace,
  replaceCharPattern,
  setApiDataToDropDownOption,
} from "../../../../helpers/utils";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useMediaQueries } from "../../../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { useSyncFormValidity } from "../../../../hooks/useSyncFormValidity/useSyncFormValidity";
import {
  siteCoreContentLoadingSelector,
  userPreferredLanguageSelector,
} from "../../../../redux/slices/appState/appStateSelector";
import { toggleLoader } from "../../../../redux/slices/appState/appStateSlice";
import {
  createClientSelector,
  editClientSelector,
} from "../../../../redux/slices/client/createClientSelector";
import { setCreateClientData } from "../../../../redux/slices/client/createClientSlice";
import { CreateClientFormKey } from "../../modelsAndSchemas/Models";
import { createClientSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { CreateClientProfileFormWrapper } from "./CreateClientProfileForm.style";

interface CreateClientProps extends FieldValues {
  clientCode?: string;
  countryVatPostalData?: CountryField[];
  isFormReset?: boolean;
}

interface CompanyIdObject {
  formName?: any;
  formLabel?: string;
  inputLength?: number;
  placeHolder?: string;
  pattern?: string;
  prefix?: string;
  isMandatory?: boolean;
}
export const pageId = PAGE_IDS.CREATE_CLIENT_PROFILE;

export const CreateClientProfileForm: FC<CreateClientProps> = ({
  clientCode,
  countryVatPostalData,
  isFormReset,
}) => {
  const {
    control,
    setValue,
    clearErrors,
    setError,
    getValues,
    formState,
    trigger,
  } = useFormContext();
  const {
    fetchLabel,
    commonLabels,
    fetchDropdownValues,
    fetchValidationMessage,
    commonValidationMessages,
  } = useSiteCore();
  const isSiteCoreLoading = useSelector(siteCoreContentLoadingSelector);

  const watchTaxVat = useWatch({
    name: CreateClientFormKey.taxVat,
  });

  const watchCountry = useWatch({
    name: [
      CreateClientFormKey.addressCountry,
      CreateClientFormKey.billingCountry,
    ],
  });

  const { commonARIALabel } = useNavigationSiteCore();

  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();

  const { isEdit: isEditClient } = useSelector(editClientSelector);
  const [isDefaultTaxVatSet, setIsDefaultTaxVatSet] = useState(false);
  const [
    isDefaultBusinessIdentificationNumberSet,
    setIsDefaultBusinessIdentificationNumberSet,
  ] = useState(false);

  const taxVatRef = useRef<{
    setDefaultValue: (defaultValue: string) => void;
  }>(null);

  const {
    createClientProfileTitle,
    companyName: companyNameKey,
    englishCompanyName: englishCompanyNameKey,
    clientType: clientTypeKey,
    industry: industryKey,
    addressCountry: addressCountryKey,
    billingCountry: billingCountryKey,
    localClientRelationshipOwner: localClientRelationshipOwnerKey,
    siretNumber: siretNumberKey,
    btwNummer: btwNummerKey,
    nifNumber: nifNumberKey,
    abnNumber: abnNumberKey,
    nzbnNumber: nzbnNumberKey,
    binNumber: binNumberKey,
    cinNumber: cinNumberKey,
    tinNumber: tinNumberKey,
    qualifiedInvoiceNumber: qualifiedInvoiceNumberKey,
    dedicatedProjectManager: dedicatedProjectManagerKey,
    originDate: originDateKey,
    vATAndABNDifferent,
    siretNumberPlaceholderText,
    siretNumberValidFormatMessage,
    bTWNummerPlaceholderText,
    bTWNummerPrefix,
    bTWNummerValidFormatMessage,
    nIFNumberPlaceholderText1,
    nIFNumberPlaceholderText2,
    nIFNumberPrefix,
    nIFNumberValidFormatMessage1,
    nIFNumberValidFormatMessage2,
    cINNumberPlaceholderText,
    cINNumberValidFormatMessage,
    tINNumberPlaceholderText,
    tINNumberValidFormatMessage,
    taxIDVATNumber: taxVatKey,
    salesOffice: salesOfficeKey,
  } = createClientSiteCoreKeys;

  const getLabel = (key: string) => {
    return fetchLabel(createClientProfileTitle, key);
  };

  const SIRET_NUMBER = getLabel(siretNumberKey);
  const BTW_NUMBER = getLabel(btwNummerKey);
  const NIF = getLabel(nifNumberKey);
  const ABN_NUMBER = getLabel(abnNumberKey);
  const NZBN = getLabel(nzbnNumberKey);
  const QUALIFIED_INVOICE_NUMBER = getLabel(qualifiedInvoiceNumberKey);
  const CIN = getLabel(cinNumberKey);
  const BIN = getLabel(binNumberKey);
  const TIN = getLabel(tinNumberKey);

  const defaultCountryData: CountryFieldDetails = {
    label: getLabel(taxVatKey),
    placeHolder: commonLabels.inputPlaceHolder,
    maxLength: 50,
    regex: ".*",
    regexNotAllowed: "",
    formatErrorMessage: "",
    blurErrorMessage: "",
  };

  const {
    FRANCE,
    SPAIN,
    NETHERLANDS,
    JAPAN,
    KOREA_DEMOCRATIC,
    KOREA_REPUBLIC,
    AUSTRALIA,
    INDIA,
    NEW_ZEALAND,
    BANGLADESH,
    SRI_LANKA,
  } = SITE_CORE_COUNTRIES_VALUE;
  const [
    localClientRelationshipOwnerMappedData,
    setLocalClientRelationshipOwnerMappedData,
  ] = useState<OptionsDataType[]>([]);
  const preferredLanguage = useSelector(userPreferredLanguageSelector);

  const [countryList, setCountryList] = useState<OptionsDataType[]>([]);

  const [englishCompanyMandatory, setEnglishCompanyMandatory] =
    useState<boolean>(false);

  const [
    dedicatedProjectManagerMappedData,
    setDedicatedProjectManagerMappedData,
  ] = useState<OptionsDataType[]>([]);

  const [tooltipMessage, setTooltipMessage] = useState<string>("");
  const [showTooltipErrorIcon, setShowTooltipErrorIcon] =
    useState<boolean>(false);

  const {
    mutateAsync: getDedicatedProjectManagersByRole,
    data: dedicatedProjectManagerData,
    isSuccess: isDedicatedProjectManagerApiSuccess,
  } = useGetUsersDetailByRoleMutation();
  const {
    data: countryListData,
    isSuccess: isCountryListApiSuccess,
    isFetching: isCountryListFetching,
  } = useGetCountryListByPage(
    PAGE_NAME_FOR_COUNTRY_DROPDOWN.CLIENT,
    isEditClient ? PAGE_OPERATION_TYPE.EDIT : PAGE_OPERATION_TYPE.ADD,
  );

  const {
    mutateAsync: getLocalClientRelationshipOwnersByRole,
    data: localClientRelationshipOwnerData,
    isSuccess: isLocalClientRelationshipOwnerApiSuccess,
  } = useGetUsersDetailByRoleMutation();

  const { data: industryListData, isFetching: isIndustryListFetching } =
    useGetIndustryListForDropdowns();
  const {
    data: salesOfficeData,
    refetchWithParams: refetchSalesOfficeData,
    isFetching: isSalesOfficeFetching,
  } = useGetDeliveryOfficeListQuery();

  useEffect(() => {
    getDedicatedProjectManagersByRole({ roleIds: [ROLE_IDS.projectManager] });
    getLocalClientRelationshipOwnersByRole({ roleIds: [ROLE_IDS.sales] });
  }, []);

  const { billingCountry, taxVat, abnNumber } = getValues();

  useEffect(() => {
    const { taxVat } = getValues();
    if (isEditClient && !isDefaultTaxVatSet && taxVat) {
      taxVatRef.current?.setDefaultValue(taxVat ?? "");
      setIsDefaultTaxVatSet(true);
    }
  }, [isEditClient, watchTaxVat]);

  const dispatch = useDispatch();
  const clientData = useSelector(createClientSelector);
  const { isMobile } = useMediaQueries();

  const { loggedInUser } = useGetLoggedInUserDetails();

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible:
          isCountryListFetching ||
          isIndustryListFetching ||
          isSalesOfficeFetching,
      }),
    );
  }, [isCountryListFetching, isIndustryListFetching, isSalesOfficeFetching]);

  useEffect(() => {
    if (!isEditClient && countryVatPostalData && loggedInUser?.countryCode) {
      const country = countryVatPostalData.find(
        (c: CountryField) =>
          c.countryKey.substring(c.countryKey.indexOf(".") + 1) ===
          loggedInUser?.countryCode.trim().toLowerCase(),
      );

      const countryName = countryList.find(
        (c: OptionsDataType) =>
          c?.value
            ?.toString()
            ?.substring(c?.value?.toString()?.indexOf(".") + 1) ===
          loggedInUser?.countryCode?.trim().toLowerCase(),
      )?.label;

      setValue(CreateClientFormKey.addressCountry, country?.countryKey);
      setValue(CreateClientFormKey.billingCountry, country?.countryKey);

      setValue(CreateClientFormKey.countryName, getValueOrBlank(countryName));
      setValue(
        CreateClientFormKey.billingCountryName,
        getValueOrBlank(countryName),
      );
      setValue(
        CreateClientFormKey.addressCountryName,
        getValueOrBlank(countryName),
      );
    }
  }, [
    isEditClient,
    loggedInUser?.countryCode,
    countryVatPostalData,
    countryList,
  ]);

  const mandatoryFields = [
    CreateClientFormKey.clientType,
    CreateClientFormKey.companyName,
    CreateClientFormKey.industry,
    CreateClientFormKey.localClientRelationshipOwnerId,
    CreateClientFormKey.addressCountry,
    CreateClientFormKey.addressCountry,
    CreateClientFormKey.siretNumber,
  ];

  const {
    formState: { errors },
  } = useFormContext();

  useSyncFormValidity({
    formKey: "createClientProfileForm",
    mandatoryFields,
    errors,
    isMobile,
  });

  const getCountryDisplayValueFromList = (country: string) => {
    if (isEmpty(country)) {
      return country;
    }
    const countryData = countryList?.find(
      (data) => data.value.toString().toLowerCase() === country.toLowerCase(),
    );
    return countryData?.label;
  };

  const handleClientTypeChange = (clientTypeId: string) => {
    dispatch(
      setCreateClientData({
        ...clientData,
        clientTypeId: clientTypeId,
      }),
    );
  };

  useEffect(() => {
    if (billingCountry !== SITE_CORE_COUNTRIES_VALUE.AUSTRALIA) return;

    const taxVatDetails = getVatPostalDetailsFromCountryKey(
      billingCountry,
      countryVatPostalData,
    )?.taxIDOrVATNumber;
    const isMaxLength = taxVat.length === taxVatDetails?.maxLength;

    if (!isMaxLength || !abnNumber) return;

    const errorType = taxVat !== abnNumber ? "custom" : undefined;
    const errorMessage =
      taxVat !== abnNumber ? getLabel(vATAndABNDifferent) : undefined;

    setTimeout(() => {
      if (errorType) {
        setError(CreateClientFormKey.taxVat, {
          type: errorType,
          message: errorMessage,
        });
      } else {
        clearErrors(CreateClientFormKey.taxVat);
      }
    }, 1000);
  }, [taxVat, abnNumber, billingCountry, countryVatPostalData]);

  useEffect(() => {
    if (
      isLocalClientRelationshipOwnerApiSuccess &&
      isNotEmpty(localClientRelationshipOwnerData)
    ) {
      const mappedData = formatApiDataToDropDownOption(
        localClientRelationshipOwnerData,
      );
      if (isNotEmpty(mappedData)) {
        setLocalClientRelationshipOwnerMappedData(mappedData);
      }
    }
  }, [
    isLocalClientRelationshipOwnerApiSuccess,
    localClientRelationshipOwnerData,
  ]);

  useEffect(() => {
    if (
      isDedicatedProjectManagerApiSuccess &&
      isNotEmpty(dedicatedProjectManagerData)
    ) {
      const mappedData = formatApiDataToDropDownOption(
        dedicatedProjectManagerData,
      );
      if (isNotEmpty(mappedData)) {
        setDedicatedProjectManagerMappedData(mappedData);
      }
    }
  }, [isDedicatedProjectManagerApiSuccess, dedicatedProjectManagerData]);

  useEffect(() => {
    if (isCountryListApiSuccess && isNotEmpty(countryListData)) {
      const mappedData = setApiDataToDropDownOption(countryListData, true);
      if (isNotEmpty(mappedData)) {
        setCountryList(mappedData);
      }
    }
  }, [isCountryListApiSuccess, countryListData]);

  useEffect(() => {
    const { errors } = formState;

    const errorTypeMatches = "matches";
    const errorMessages: Record<string, string> = {
      [CreateClientFormKey.siretNumber]: getLabel(
        siretNumberValidFormatMessage,
      ),
      [CreateClientFormKey.btwNummer]: getLabel(bTWNummerValidFormatMessage),
      [CreateClientFormKey.nif]: checkWithLowerCase(
        getValues().billingCountry,
        SPAIN,
      )
        ? getLabel(nIFNumberValidFormatMessage1)
        : getLabel(nIFNumberValidFormatMessage2),
      [CreateClientFormKey.nzbn]: fetchValidationMessage(
        commonSiteCoreKeys.mustBe13Digits,
      ),
      [CreateClientFormKey.abnNumber]: fetchValidationMessage(
        commonSiteCoreKeys.mustBe11Digits,
      ),
      [CreateClientFormKey.qualifiedInvoiceNumber]: fetchValidationMessage(
        commonSiteCoreKeys.mustBe13Digits,
      ),
      [CreateClientFormKey.bin]: fetchValidationMessage(
        commonSiteCoreKeys.mustBe13Digits,
      ),
      [CreateClientFormKey.tin]: getLabel(tINNumberValidFormatMessage),
      [CreateClientFormKey.cin]: getLabel(cINNumberValidFormatMessage),
    };

    const errorKey =
      errors &&
      Object.keys(errorMessages).find(
        (key) => errors[key]?.type === errorTypeMatches,
      );
    if (errorKey) {
      setTooltipMessage(errorMessages[errorKey]);
      setShowTooltipErrorIcon(true);
    } else {
      setShowTooltipErrorIcon(false);
    }
  }, [formState]);

  const getCompanyIdLabel = (
    name: string,
    addressCountry?: string,
    billingCountry?: string,
  ): CompanyIdObject => {
    let lengthBtw = MAX_LENGTH_14;
    let placeHolderBtw = getLabel(bTWNummerPlaceholderText);
    if (name === BTW_NUMBER) {
      const isNetherlands = (country: string) =>
        checkWithLowerCase(country, NETHERLANDS);
      if (
        billingCountry &&
        ((addressCountry &&
          !isNetherlands(addressCountry) &&
          isNetherlands(billingCountry)) ||
          (addressCountry &&
            isNetherlands(addressCountry) &&
            !isNetherlands(billingCountry)))
      ) {
        lengthBtw = MAX_LENGTH_50;
        placeHolderBtw = commonLabels.inputPlaceHolder;
      }
    }

    const formData: Record<string, CompanyIdObject> = {
      [SIRET_NUMBER]: {
        formName: CreateClientFormKey.siretNumber,
        formLabel: SIRET_NUMBER,
        inputLength: MAX_LENGTH_14_INCLUDING_SPACE,
        placeHolder: getLabel(siretNumberPlaceholderText),
        pattern: PATTERN.SIRET_NUMBER,
        isMandatory: true,
      },
      [BTW_NUMBER]: {
        formName: CreateClientFormKey.btwNummer,
        formLabel: BTW_NUMBER,
        inputLength: lengthBtw,
        placeHolder: placeHolderBtw,
        prefix: getLabel(bTWNummerPrefix),
      },
      [NIF]: {
        formName: CreateClientFormKey.nif,
        formLabel: NIF,
        inputLength: MAX_LENGTH_11,
        placeHolder: getLabel(nIFNumberPlaceholderText2),
        prefix: getLabel(nIFNumberPrefix),
      },
      [ABN_NUMBER]: {
        formName: CreateClientFormKey.abnNumber,
        formLabel: ABN_NUMBER,
        inputLength: MAX_LENGTH_11,
        placeHolder: commonLabels.inputPlaceHolder,
      },
      [NZBN]: {
        formName: CreateClientFormKey.nzbn,
        formLabel: NZBN,
        inputLength: MAX_LENGTH_13,
        placeHolder: commonLabels.inputPlaceHolder,
      },
      [QUALIFIED_INVOICE_NUMBER]: {
        formName: CreateClientFormKey.qualifiedInvoiceNumber,
        formLabel: QUALIFIED_INVOICE_NUMBER,
        inputLength: MAX_LENGTH_13,
        placeHolder: commonLabels.inputPlaceHolder,
      },
      [CIN]: {
        formName: CreateClientFormKey.cin,
        formLabel: CIN,
        inputLength: MAX_LENGTH_21,
        placeHolder: getLabel(cINNumberPlaceholderText),
      },
      [BIN]: {
        formName: CreateClientFormKey.bin,
        formLabel: BIN,
        inputLength: MAX_LENGTH_13,
        placeHolder: commonLabels.inputPlaceHolder,
      },
      [TIN]: {
        formName: CreateClientFormKey.tin,
        formLabel: TIN,
        inputLength: MAX_LENGTH_9,
        placeHolder: getLabel(tINNumberPlaceholderText),
      },
    };

    return formData[name] || {};
  };

  const clearCountryFormError = () => {
    setValue(CreateClientFormKey.siretNumber, "");
    setValue(CreateClientFormKey.btwNummer, "");
    setValue(CreateClientFormKey.nif, "");
    setValue(CreateClientFormKey.abnNumber, "");
    setValue(CreateClientFormKey.nzbn, "");
    setValue(CreateClientFormKey.qualifiedInvoiceNumber, "");
    setValue(CreateClientFormKey.cin, "");
    setValue(CreateClientFormKey.bin, "");
    setValue(CreateClientFormKey.tin, "");
    clearErrors(CreateClientFormKey.siretNumber);
    clearErrors(CreateClientFormKey.btwNummer);
    clearErrors(CreateClientFormKey.nif);
    clearErrors(CreateClientFormKey.abnNumber);
    clearErrors(CreateClientFormKey.nzbn);
    clearErrors(CreateClientFormKey.qualifiedInvoiceNumber);
    clearErrors(CreateClientFormKey.cin);
    clearErrors(CreateClientFormKey.bin);
    clearErrors(CreateClientFormKey.tin);
  };

  const handleSetEnglishCountryMandatory = () => {
    const { addressCountry, billingCountry } = getValues();
    if (
      checkWithLowerCase(addressCountry, JAPAN) ||
      checkWithLowerCase(billingCountry, JAPAN) ||
      checkWithLowerCase(addressCountry, KOREA_DEMOCRATIC) ||
      checkWithLowerCase(billingCountry, KOREA_DEMOCRATIC) ||
      checkWithLowerCase(addressCountry, KOREA_REPUBLIC) ||
      checkWithLowerCase(billingCountry, KOREA_REPUBLIC) ||
      preferredLanguage === LANGUAGE_CODE.JA_JP
    )
      setEnglishCompanyMandatory(true);
    else setEnglishCompanyMandatory(false);
  };

  const onCountryChange = (isAddressCountry: boolean) => {
    if (!isAddressCountry) {
      setValue(CreateClientFormKey.taxVat, "");
      clearErrors(CreateClientFormKey.taxVat);
    } else {
      setValue(CreateClientFormKey.postalCode, "");
      clearErrors(CreateClientFormKey.postalCode);
    }

    setValue(CreateClientFormKey.stateProvince, "");
    clearErrors(CreateClientFormKey.stateProvince);

    clearCountryFormError();
    const { addressCountry, billingCountry } = getValues();
    if (isNotEmpty(addressCountry)) {
      const selectedAddressCountryLabel =
        getCountryDisplayValueFromList(addressCountry);
      if (isNotEmpty(selectedAddressCountryLabel)) {
        setValue(CreateClientFormKey.countryName, selectedAddressCountryLabel, {
          shouldValidate: true,
        });
        setValue(CreateClientFormKey.country, addressCountry, {
          shouldValidate: true,
        });

        setValue(
          CreateClientFormKey.addressCountryName,
          selectedAddressCountryLabel,
          {
            shouldValidate: true,
          },
        );
      }
    }

    if (isNotEmpty(billingCountry)) {
      const selectedBillingCountryLabel =
        getCountryDisplayValueFromList(billingCountry);
      if (isNotEmpty(selectedBillingCountryLabel)) {
        setValue(
          CreateClientFormKey.billingCountryName,
          selectedBillingCountryLabel,
          {
            shouldValidate: true,
          },
        );
      }
    }
    handleSetEnglishCountryMandatory();
  };

  useEffect(() => {
    if (!englishCompanyMandatory && formState.errors?.englishCompanyName) {
      clearErrors(CreateClientFormKey.englishCompanyName);
    }
  }, [englishCompanyMandatory]);

  useEffect(() => {
    handleSetEnglishCountryMandatory();
  }, [preferredLanguage, watchCountry]);

  // Helper functions to reduce complexity
  const getFormDataForFrance = () => getCompanyIdLabel(SIRET_NUMBER);
  const getFormDataForNetherlands = (
    addressCountry?: string,
    billingCountry?: string,
  ) => getCompanyIdLabel(BTW_NUMBER, addressCountry, billingCountry);
  const getFormDataForSpain = (billingCountry?: string) => {
    const data = getCompanyIdLabel(NIF);
    if (isEmpty(billingCountry)) {
      data.placeHolder = getLabel(nIFNumberPlaceholderText2);
    }
    return data;
  };
  const getFormDataForAustralia = () => getCompanyIdLabel(ABN_NUMBER);
  const getFormDataForNewZealand = () => getCompanyIdLabel(NZBN);
  const getFormDataForJapan = () => getCompanyIdLabel(QUALIFIED_INVOICE_NUMBER);
  const getFormDataForIndia = () => getCompanyIdLabel(CIN);
  const getFormDataForBangladesh = () => getCompanyIdLabel(BIN);
  const getFormDataForSriLanka = () => getCompanyIdLabel(TIN);

  // Helper functions for country-specific logic
  const isFrance = (country?: string) => checkWithLowerCase(country, FRANCE);
  const isNetherlands = (country?: string) =>
    checkWithLowerCase(country, NETHERLANDS);
  const isSpain = (country?: string) => checkWithLowerCase(country, SPAIN);
  const isAustralia = (country?: string) =>
    checkWithLowerCase(country, AUSTRALIA);
  const isNewZealand = (country?: string) =>
    checkWithLowerCase(country, NEW_ZEALAND);
  const isJapan = (country?: string) => checkWithLowerCase(country, JAPAN);
  const isIndia = (country?: string) => checkWithLowerCase(country, INDIA);
  const isBangladesh = (country?: string) =>
    checkWithLowerCase(country, BANGLADESH);
  const isSriLanka = (country?: string) =>
    checkWithLowerCase(country, SRI_LANKA);

  const getFormDataForSpainSpecial = (billingCountry?: string) => {
    const data = getCompanyIdLabel(NIF);
    data.placeHolder = getLabel(nIFNumberPlaceholderText1);
    return data;
  };

  const getFormDataForCompanyId = (
    addressCountry?: string,
    billingCountry?: string,
  ): CompanyIdObject => {
    if (!addressCountry) return {};

    if (isFrance(addressCountry)) {
      return getFormDataForFrance();
    }
    if (isNetherlands(addressCountry)) {
      return getFormDataForNetherlands(addressCountry, billingCountry);
    }
    if (isSpain(addressCountry)) {
      if (
        billingCountry &&
        isSpain(billingCountry) &&
        !isFrance(addressCountry)
      ) {
        return getFormDataForSpainSpecial(billingCountry);
      }
      return getFormDataForSpain(billingCountry);
    }
    if (isAustralia(addressCountry)) {
      return getFormDataForAustralia();
    }
    if (isNewZealand(addressCountry)) {
      return getFormDataForNewZealand();
    }
    if (isJapan(addressCountry)) {
      return getFormDataForJapan();
    }
    if (isIndia(addressCountry)) {
      return getFormDataForIndia();
    }
    if (isBangladesh(addressCountry)) {
      return getFormDataForBangladesh();
    }
    if (isSriLanka(addressCountry)) {
      return getFormDataForSriLanka();
    }
    return {};
  };

  const getSiretNumberInputProps = (formData: CompanyIdObject) => {
    return {
      onInput: (e: React.FormEvent<HTMLInputElement>) => {
        const target = e.target as HTMLInputElement;
        let input = removeSpecialCharacters(target.value);

        const regCheck = REGEX_ALLOW_ALPHABET_SPECIAL_CHAR;
        regCheck.lastIndex = 0;
        const regTest = regCheck.test(input);

        if (regTest) {
          setTimeout(() => {
            setError(formData.formName, {
              type: "custom",
              message: fetchValidationMessage(
                commonSiteCoreKeys.alphabetNotAllowedValidator,
              ),
            });
          }, FORM_ERROR_TIMEOUT_SHORT);
        } else {
          clearErrors(formData.formName);
        }

        input = input
          .toUpperCase()
          .replace(REGEX_ALLOW_NUMBER, "")
          .slice(0, formData.inputLength);

        const formattedObject = replaceCharPattern(
          getValueOrBlank(formData.pattern),
          input,
        );

        let formattedNumber =
          formattedObject.formattedText +
          formattedObject.remainingText.join("");
        target.value = formattedNumber.toUpperCase();
      },
      onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
        const target = e.target as HTMLInputElement;

        if (formData.isMandatory && isEmpty(target.value)) {
          setTimeout(() => {
            setError(formData.formName, {
              type: "custom",
              message: fetchValidationMessage(
                commonSiteCoreKeys.requiredValidation,
              ),
            });
          }, FORM_ERROR_TIMEOUT_SHORT);
        }
        if (target.value.length < (formData.inputLength ?? MAX_LENGTH_14))
          return;

        clearErrors(formData.formName);

        const regCheck = REGEX_ALL_ZERO;
        regCheck.lastIndex = 0;
        const regTest = regCheck.test(
          removeWhiteSpace(target.value.toUpperCase()),
        );

        if (regTest) {
          setTimeout(() => {
            setError(formData.formName, {
              type: "custom",
              message: fetchValidationMessage(
                commonSiteCoreKeys.greaterThanZeroFieldValidator,
              ),
            });
          }, FORM_ERROR_TIMEOUT_SHORT);
        }
      },
    };
  };

  const getAbnNumberInputProps = (formData: CompanyIdObject) => {
    return {
      onInput: (e: React.FormEvent<HTMLInputElement>) => {
        const target = e.target as HTMLInputElement;

        const regCheck = REGEX_ALLOW_ALPHABET_SPECIAL_CHAR;
        regCheck.lastIndex = 0;
        const regTest = regCheck.test(target.value);

        if (regTest) {
          setTimeout(() => {
            setError(formData.formName, {
              type: "custom",
              message: fetchValidationMessage(
                commonSiteCoreKeys.alphabetOrSpecialCharNotAllowed,
              ),
            });
          }, FORM_ERROR_TIMEOUT_SHORT);
        } else {
          clearErrors(formData.formName);
        }

        target.value = target.value
          .toUpperCase()
          .replace(REGEX_ALLOW_NUMBER, "")
          .slice(0, formData.inputLength);
        setValue(formData.formName, target.value);
      },
      onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
        const target = e.target as HTMLInputElement;
        if (target.value.length < (formData.inputLength ?? MAX_LENGTH_11))
          return;

        target.value = target.value.toUpperCase();
        setValue(formData.formName, target.value);
        clearErrors(formData.formName);

        const regCheck = REGEX_ALL_ZERO;
        regCheck.lastIndex = 0;
        const regTest = regCheck.test(
          removeWhiteSpace(target.value.toUpperCase()),
        );

        setTimeout(() => {
          if (regTest) {
            setError(formData.formName, {
              type: "custom",
              message: fetchValidationMessage(
                commonSiteCoreKeys.greaterThanZeroFieldValidator,
              ),
            });
          }
        }, FORM_ERROR_TIMEOUT_SHORT);
      },
    };
  };

  const getBTWNummerInputProps = (formData: CompanyIdObject) => {
    const prefix = getValueOrBlank(formData.prefix);
    return {
      onInput: (e: React.FormEvent<HTMLInputElement>) => {
        const target = e.target as HTMLInputElement;
        let input = target.value;
        if (!input.startsWith(prefix)) {
          input = prefix + input.substring(prefix.length);
        }

        const regCheck = REGEX_ALLOW_SPECIAL_CHAR;
        regCheck.lastIndex = 0;
        const regTest = regCheck.test(target.value);
        if (regTest) {
          setTimeout(() => {
            setError(formData.formName, {
              type: "custom",
              message: fetchValidationMessage(
                commonSiteCoreKeys.specialCharNotAllowed,
              ),
            });
          }, FORM_ERROR_TIMEOUT_SHORT);
        } else {
          clearErrors(formData.formName);
        }
        target.value = input
          .toUpperCase()
          .replace(REGEX_ALLOW_ALPHA_NUMERIC_WITHOUT_SPECIAL_CHAR, "")
          .slice(0, formData.inputLength);
        setValue(formData.formName, target.value);
      },
      onBlur: (e: React.FormEvent<HTMLInputElement>) => {
        const target = e.target as HTMLInputElement;
        let input = target.value;
        if (input === prefix) {
          input = "";
          setValue(formData.formName, input);
        }
      },
      onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
        const target = e.target as HTMLInputElement;
        let input = target.value;
        if (!input.startsWith(getValueOrBlank(prefix))) {
          input = prefix + input.substring(prefix.length);
        }
        setValue(formData.formName, input);
      },
    };
  };

  const addressCountryWatch = useWatch({
    control,
    name: CreateClientFormKey.addressCountry,
  });
  const billingCountryWatch = useWatch({
    control,
    name: CreateClientFormKey.billingCountry,
  });
  const businessIdentificationNumberWatch = useWatch({
    control,
    name: CreateClientFormKey.businessIdentificationNumber,
  });

  const salesOfficeWatch = useWatch({
    control,
    name: CreateClientFormKey.salesOfficeId,
  });

  const renderCompanyIdLabel = useCallback(() => {
    const { addressCountry, billingCountry, businessIdentificationNumber } =
      getValues();

    let formData = getFormDataForCompanyId(addressCountry, billingCountry);

    let inputProps = {};
    if (formData.formName === CreateClientFormKey.siretNumber) {
      inputProps = getSiretNumberInputProps(formData);
    } else if (
      [
        CreateClientFormKey.abnNumber,
        CreateClientFormKey.nzbn,
        CreateClientFormKey.qualifiedInvoiceNumber,
        CreateClientFormKey.bin,
      ].includes(formData.formName)
    ) {
      inputProps = getAbnNumberInputProps(formData);
    } else if (
      !isEmpty(billingCountry) &&
      formData.formName === CreateClientFormKey.btwNummer &&
      checkWithLowerCase(addressCountry, NETHERLANDS) &&
      !checkWithLowerCase(billingCountry, NETHERLANDS)
    ) {
      // Intentionally left blank: no inputProps for this case
    } else if (
      [
        CreateClientFormKey.btwNummer,
        CreateClientFormKey.nif,
        CreateClientFormKey.cin,
        CreateClientFormKey.tin,
      ].includes(formData.formName)
    ) {
      inputProps = getBTWNummerInputProps(formData);
    }
    if (
      formData.formName &&
      isEditClient &&
      !isDefaultBusinessIdentificationNumberSet &&
      businessIdentificationNumber
    ) {
      const formattedObject = replaceCharPattern(
        getValueOrBlank(formData.pattern),
        businessIdentificationNumber,
      );

      let formattedNumber =
        formattedObject.formattedText + formattedObject.remainingText.join("");

      setTimeout(() => {
        setValue(formData.formName, formattedNumber.toUpperCase());
      }, FORM_ERROR_TIMEOUT_LONG);
      setIsDefaultBusinessIdentificationNumberSet(true);
    }

    return formData.formName ? (
      <MuiGrid
        size={gridSize}
        className={`${gridSizeMediumClass} company-id-number`}
      >
        <FormController control={control} name={formData.formName}>
          <MuiTextField
            id={`${pageId}-${formData.formName}`}
            label={formData.formLabel}
            placeholder={formData.placeHolder}
            {...inputProps}
            maxLength={formData.inputLength}
            isErrorInfoIconEnabled={showTooltipErrorIcon}
            tooltipMessage={tooltipMessage}
            requiredARIALabel={commonARIALabel.required}
            isMandatory={formData.isMandatory}
          />
        </FormController>
      </MuiGrid>
    ) : null;
  }, [
    addressCountryWatch,
    billingCountryWatch,
    businessIdentificationNumberWatch,
    isDefaultBusinessIdentificationNumberSet,
    showTooltipErrorIcon,
    isSiteCoreLoading,
  ]);

  const callSalesOfficeApi = (countryKey: string) => {
    refetchSalesOfficeData(getCountryCodeWithoutCountryPrefix(countryKey));
  };

  useEffect(() => {
    const { addressCountry } = getValues();
    setValue(CreateClientFormKey.salesOfficeId, "");
    setValue(CreateClientFormKey.salesOfficeName, "");
    callSalesOfficeApi(addressCountry);
  }, [addressCountryWatch]);

  useEffect(() => {
    const { salesOfficeId } = getValues();
    if (salesOfficeId) {
      const selectedSalesOffice = salesOfficeData?.find(
        (office: DeliveryOfficeListItem) => office.id === salesOfficeId,
      );

      setValue(
        CreateClientFormKey.salesOfficeName,
        getValueOrBlank(selectedSalesOffice?.name),
      );
    } else {
      setValue(CreateClientFormKey.salesOfficeName, "");
    }
  }, [salesOfficeWatch]);

  const handleCompanyNameChange = (companyName: string) => {
    !englishCompanyMandatory &&
      setValue(CreateClientFormKey.englishCompanyName, companyName);
  };

  const handleCompanyNameBlur = async () => {
    !englishCompanyMandatory &&
      (await trigger(CreateClientFormKey.englishCompanyName));
  };

  return (
    <CreateClientProfileFormWrapper>
      <form>
        <MuiGrid className="main-container">
          <MuiGrid container>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateClientFormKey.clientType}
              >
                <MuiSelect
                  visibleDefaultSelectOption
                  id={`${pageId}-${CreateClientFormKey.clientType}`}
                  isMandatory
                  label={getLabel(clientTypeKey)}
                  onChange={(e) =>
                    handleClientTypeChange(e.target.value as string)
                  }
                  options={fetchDropdownValues(
                    createClientSiteCoreKeys.createClientProfileTitle,
                    createClientSiteCoreKeys.clientTypeList,
                  )}
                  placeholder={commonLabels.selectPlaceHolder}
                  disabled={!!clientCode}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateClientFormKey.companyName}
              >
                <MuiTextField
                  isMandatory
                  id={`${pageId}-${CreateClientFormKey.companyName}`}
                  label={getLabel(companyNameKey)}
                  placeholder={commonLabels.inputPlaceHolder}
                  maxLength={MAX_LENGTH_100}
                  requiredARIALabel={commonARIALabel.required}
                  tooltipMessage={
                    commonValidationMessages.fieldSpecialCharactersTooltipMessage
                  }
                  tooltipHeaderTitle={
                    commonValidationMessages.allowedSpecialCharactersTitle
                  }
                  onChange={(event) =>
                    handleCompanyNameChange(event.target.value)
                  }
                  onBlur={handleCompanyNameBlur}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateClientFormKey.englishCompanyName}
              >
                <MuiTextField
                  isMandatory={englishCompanyMandatory}
                  id={`${pageId}-${CreateClientFormKey.englishCompanyName}`}
                  label={getLabel(englishCompanyNameKey)}
                  placeholder={commonLabels.inputPlaceHolder}
                  maxLength={MAX_LENGTH_100}
                  requiredARIALabel={commonARIALabel.required}
                  isErrorInfoIconEnabled
                  tooltipMessage={
                    commonValidationMessages.fieldSpecialCharactersTooltipMessage
                  }
                  tooltipHeaderTitle={
                    commonValidationMessages.allowedSpecialCharactersTitle
                  }
                />
              </FormController>
            </MuiGrid>

            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateClientFormKey.industry}
              >
                <MuiSelect
                  isMandatory
                  id={`${pageId}-${CreateClientFormKey.industry}`}
                  label={getLabel(industryKey)}
                  visibleDefaultSelectOption
                  showSearch
                  options={setApiDataToDropDownOption(industryListData)}
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateClientFormKey.localClientRelationshipOwnerId}
              >
                <MuiSelect
                  isMandatory
                  id={`${pageId}-${CreateClientFormKey.localClientRelationshipOwnerId}`}
                  label={getLabel(localClientRelationshipOwnerKey)}
                  visibleDefaultSelectOption
                  showSearch
                  options={localClientRelationshipOwnerMappedData}
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
          </MuiGrid>
          <MuiBox className="separator" />
          <MuiGrid container>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateClientFormKey.addressCountry}
              >
                <MuiSelect
                  id={`${pageId}-${CreateClientFormKey.addressCountry}`}
                  isMandatory
                  label={getLabel(addressCountryKey)}
                  onChange={() => onCountryChange(true)}
                  visibleDefaultSelectOption={true}
                  placeholder={commonLabels.selectPlaceHolder}
                  options={countryList}
                  requiredARIALabel={commonARIALabel.required}
                  showSearch
                  searchPlaceholder={commonLabels.search}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateClientFormKey.billingCountry}
              >
                <MuiSelect
                  id={`${pageId}-${CreateClientFormKey.billingCountry}`}
                  label={getLabel(billingCountryKey)}
                  onChange={() => onCountryChange(false)}
                  isMandatory
                  disabled={!!clientCode}
                  visibleDefaultSelectOption={true}
                  placeholder={commonLabels.selectPlaceHolder}
                  options={countryList}
                  requiredARIALabel={commonARIALabel.required}
                  showSearch
                  searchPlaceholder={commonLabels.search}
                />
              </FormController>
            </MuiGrid>

            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateClientFormKey.salesOfficeId}
              >
                <MuiSelect
                  isMandatory
                  id={`${pageId}-${CreateClientFormKey.salesOfficeId}`}
                  label={getLabel(salesOfficeKey)}
                  visibleDefaultSelectOption
                  showSearch
                  options={setApiDataToDropDownOption(salesOfficeData)}
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>

            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateClientFormKey.taxVat}
                ref={taxVatRef}
              >
                <TextFieldWithValidation
                  id={`${pageId}-${CreateClientFormKey.taxVat}`}
                  formId={CreateClientFormKey.taxVat}
                  zeroValidationMessage={fetchValidationMessage(
                    commonSiteCoreKeys.greaterThanZeroFieldValidator,
                  )}
                  requiredFieldMessage={fetchValidationMessage(
                    commonSiteCoreKeys.requiredValidation,
                  )}
                  isCountryChanged={billingCountry}
                  fieldDetails={{
                    ...(getVatPostalDetailsFromCountryKey(
                      billingCountry,
                      countryVatPostalData,
                    )?.taxIDOrVATNumber || defaultCountryData),
                  }}
                  isFormReset={isFormReset}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            {renderCompanyIdLabel()}
          </MuiGrid>

          <MuiBox className="separator dashed" />
          <MuiGrid container>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateClientFormKey.dedicatedProjectManager}
              >
                <MuiSelect
                  id={`${pageId}-${CreateClientFormKey.dedicatedProjectManager}`}
                  label={getLabel(dedicatedProjectManagerKey)}
                  visibleDefaultSelectOption
                  showSearch
                  options={dedicatedProjectManagerMappedData}
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormController
                control={control}
                name={CreateClientFormKey.originDate}
              >
                <MuiTextField
                  disabled
                  id={`${pageId}-${CreateClientFormKey.originDate}`}
                  label={getLabel(originDateKey)}
                  placeholder={commonLabels.dateInputPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
          </MuiGrid>
        </MuiGrid>
      </form>
    </CreateClientProfileFormWrapper>
  );
};
